import React from 'react';
import { connect } from 'react-redux';
import { useFormContext } from 'react-hook-form';

import CustomSwitchButton from 'components/base/CustomSwitchButton';
import CustomTitleWithInput from 'components/base/CustomTitleWithInput';
import { CustomTitleLabel } from 'components/earning/CustomBaseComponments';

import {
  DISPLAY_LEVEL_DOWNGRADE_LOGIC,
  DISPLAY_LEVEL_RENEWAL_LOGIC,
  LEVEL_CALCULATION_SOURCE,
  LEVEL_DOWNGRADE_LOGIC,
  LEVEL_RENEWAL_LOGIC
} from 'config/CustomEnums';
import { SectionTopTitle } from 'components/base/CommonComponent';
import { LevelErrorHandleFields } from 'containers/engagement/level/LevelErrorHandle';
import ErrorFieldMessage, { hasError } from 'components/base/ErrorFieldMessage';
import ReactHookFormNumberMutipleInputWithUnit from 'components/reactHookFormComponents/ReactHookFormNumberMutipleInputWithUnit';
import { DropDownWithDefaultSelect } from 'components/base/ActionsDropdown';
import { CustomTitleWithDropDown } from 'components/customer/CustomTitleWithDropDown';
import './RenewalAndDowngradeSection.scss';


const RenewalAndDowngradeSection = ({
  levelList,
}) => {
  const {
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();

  const levelCalculationSourceField = "levelCalculationSource";
  const isLowestLevelField = "isLowestLevel";
  const levelRankField = "levelRank";
  const displayRenewalLogicField = "displayRenewalLogic";
  const enableCheckCalculationSourceValueWithinPeriodForRetainField = "enableCheckCalculationSourceValueWithinPeriodForRetain";
  const retainRequiredValueField = "retainRequiredValue";
  const enableCheckEffectiveTransactionTimesWithinPeriodField = "enableCheckEffectiveTransactionTimesWithinPeriod";
  const requiredTransactionTimesField = "requiredTransactionTimes";
  const minimumAmountOfOneTransactionForRenewField = "minimumAmountOfOneTransactionForRenew";
  const displayDowngradeLogicField = "displayDowngradeLogic";
  const minimumDowngradeLevelField = "dropdownMinimumDowngradeLevel";
  const renewForceDowngradeLevelField = "dropdownDowngradeLevel";

  const watchLevelCalculationSource = watch(levelCalculationSourceField);
  const watchIsLowestLevel = watch(isLowestLevelField);
  const watchLevelRank = watch(levelRankField);
  const watchDisplayRenewalLogic = watch(displayRenewalLogicField);
  const watchenableCheckCalculationSourceValueWithinPeriodForRetain = watch(enableCheckCalculationSourceValueWithinPeriodForRetainField);
  const watchRetainRequiredValue = watch(retainRequiredValueField);
  const watchEnableCheckEffectiveTransactionTimesWithinPeriod = watch(enableCheckEffectiveTransactionTimesWithinPeriodField);
  const watchRequiredTransactionTimes = watch(requiredTransactionTimesField);
  const watchMinimumAmountOfOneTransactionForRenew = watch(minimumAmountOfOneTransactionForRenewField);
  const watchDisplayDowngradeLogic = watch(displayDowngradeLogicField);
  const watchMinimumDowngradeLevel = watch(minimumDowngradeLevelField);
  const watchRenewForceDowngradeLevel = watch(renewForceDowngradeLevelField);

  const isCalculatePP = watchLevelCalculationSource === LEVEL_CALCULATION_SOURCE.pp;

  const renewalLogicOptions = [
    DISPLAY_LEVEL_RENEWAL_LOGIC[LEVEL_RENEWAL_LOGIC.retainCurrentLevel],
    DISPLAY_LEVEL_RENEWAL_LOGIC[LEVEL_RENEWAL_LOGIC.checkRetainRequirement],
    DISPLAY_LEVEL_RENEWAL_LOGIC[LEVEL_RENEWAL_LOGIC.forceDowngrade],
  ].map((logic) => ({
    name: logic,
    action: () => {
      setValue(displayRenewalLogicField, logic, { shouldDirty: true });
    },
  }));

  const isCheckRetainRequirement = watchDisplayRenewalLogic === DISPLAY_LEVEL_RENEWAL_LOGIC[LEVEL_RENEWAL_LOGIC.checkRetainRequirement];
  const isForceDowngrade = watchDisplayRenewalLogic === DISPLAY_LEVEL_RENEWAL_LOGIC[LEVEL_RENEWAL_LOGIC.forceDowngrade];

  let renewalLogicDescription = "Members can retain current level in next membership period unconditionally.";
  if (isCheckRetainRequirement) {
    renewalLogicDescription = "Current level can be retained if either one of the following conditions is met. Otherwise, the member will be downgraded.";
  }
  if (isForceDowngrade) {
    renewalLogicDescription = "All members of this level will be downgraded to a specific level.";
  }

  const downgradeLogicOptions = [
    DISPLAY_LEVEL_DOWNGRADE_LOGIC[LEVEL_DOWNGRADE_LOGIC.checkEntryRequirements],
    DISPLAY_LEVEL_DOWNGRADE_LOGIC[LEVEL_DOWNGRADE_LOGIC.downgradeToSpecificLevel],
  ].map((logic) => ({
    name: logic,
    action: () => {
      setValue(displayDowngradeLogicField, logic, { shouldDirty: true });
    },
  }));

  const isCheckEntryRequirements = watchDisplayDowngradeLogic === DISPLAY_LEVEL_DOWNGRADE_LOGIC[LEVEL_DOWNGRADE_LOGIC.checkEntryRequirements];
  const isDowngradeToSpecificLevel = watchDisplayDowngradeLogic === DISPLAY_LEVEL_DOWNGRADE_LOGIC[LEVEL_DOWNGRADE_LOGIC.downgradeToSpecificLevel];

  const validLevelList = levelList?.filter(level => level.active && watchLevelRank && (level.levelRank < watchLevelRank))

  return (
    <>
      <SectionTopTitle title="RENEWAL & DOWNGRADE SETTINGS" />

      <CustomTitleLabel title="Renewal logic" />
      <DropDownWithDefaultSelect
        className='level-common-dropdown'
        actions={renewalLogicOptions}
        defaultValue={watchDisplayRenewalLogic}
        disabled={watchIsLowestLevel}
      />
      <div className="second-section_description">
        {renewalLogicDescription}
      </div>

      {
        isCheckRetainRequirement && (
          <>
            <div className="content-area">
              <CustomTitleLabel title={
                `Condition 1: Accumulated ${isCalculatePP ? "PP" : "spending"} within membership period of current level`
              } />
              <div className='renewal-and-downgrade-section-div'>
                <CustomSwitchButton
                  defaultChecked={watchenableCheckCalculationSourceValueWithinPeriodForRetain}
                  onChange={(isChecked) => {
                    setValue(enableCheckCalculationSourceValueWithinPeriodForRetainField, isChecked, { shouldDirty: true });
                  }}
                />
              </div>
              <ErrorFieldMessage
                id={enableCheckCalculationSourceValueWithinPeriodForRetainField}
                error={hasError(errors, enableCheckCalculationSourceValueWithinPeriodForRetainField)}
                message={LevelErrorHandleFields.noCondition}
              />
              <CustomTitleWithInput
                type="number"
                customClass="level-common-input"
                unit={isCalculatePP ? "PP" : "HK$"}
                disabled={!watchenableCheckCalculationSourceValueWithinPeriodForRetain}
                defaultValue={watchRetainRequiredValue}
                setValue={(value) => {
                  setValue(retainRequiredValueField, value, { shouldDirty: true, });
                }}
                error={{
                  id: retainRequiredValueField,
                  error: hasError(errors, retainRequiredValueField),
                  message: hasError(errors, retainRequiredValueField),
                }}
              />
            </div>

            <div className="content-area">
              <CustomTitleLabel title="Condition 2: X times of transactions upon certain amount within membership period of current level" />
              <div className='renewal-and-downgrade-section-div'>
                <CustomSwitchButton
                  defaultChecked={watchEnableCheckEffectiveTransactionTimesWithinPeriod}
                  onChange={(isChecked) => {
                    setValue(enableCheckEffectiveTransactionTimesWithinPeriodField, isChecked, { shouldDirty: true });
                  }}
                />
              </div>
              <ReactHookFormNumberMutipleInputWithUnit
                disabled={!watchEnableCheckEffectiveTransactionTimesWithinPeriod}
                per="times of transaction(s) and each at least"
                unit="HK$"
                firstValueFieldName="requiredTransactionTimes"
                secondValueFieldName="minimumAmountOfOneTransactionForRenew"
                errorId="requiredTransactionTimes"
              />
            </div>

            <CustomTitleLabel title="Downgrade logic" />
            <DropDownWithDefaultSelect
              className='level-common-dropdown'
              actions={downgradeLogicOptions}
              defaultValue={watchDisplayDowngradeLogic}
            />
            <ErrorFieldMessage
              id={displayDowngradeLogicField}
              error={hasError(errors, displayDowngradeLogicField)}
              message={LevelErrorHandleFields.downgradeCheckConditionOne}
            />
          </>
        )
      }

      {
        isCheckRetainRequirement && isCheckEntryRequirements && (
          <CustomTitleWithDropDown
            title="Lowest level can be downgraded to (optional)"
            source={validLevelList}
            defaultValue={watchMinimumDowngradeLevel}
            setValue={(value) => {
              setValue(minimumDowngradeLevelField, value, { shouldDirty: true });
            }}
            needFilter={false}
          />
        )
      }

      {
        (
          isForceDowngrade
          || (isCheckRetainRequirement && isDowngradeToSpecificLevel)
        ) && (
          <>
            <CustomTitleWithDropDown
              title="Downgrade to"
              source={validLevelList}
              defaultValue={watchRenewForceDowngradeLevel}
              setValue={(value) => {
                setValue(renewForceDowngradeLevelField, value, { shouldDirty: true });
              }}
              needFilter={false}
            />
            <ErrorFieldMessage
              id={renewForceDowngradeLevelField}
              error={hasError(errors, renewForceDowngradeLevelField)}
              message={LevelErrorHandleFields.level}
            />

          </>
        )
      }

    </>
  );
}

const mapPropsToState = (state) => ({
  levelList: state.levels?.pagedList,
});

export default connect(mapPropsToState)(RenewalAndDowngradeSection);
